/**
 * Session form (field event).
 */
import * as React from 'react';
import { X } from 'lucide-react';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toLocalDateString } from '@/utils/format-date-utils';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { Checkbox } from '@/components/forms/Checkbox';
import { Button } from '@/components/forms/Button';
import { Textarea } from '@/components/forms/Textarea';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useAuthStore } from '@/stores/authStore';
import { getSessionTypesForTab } from '@/utils/common-utils';
import {
  BUDGET_POST_SELECT_OPTIONS,
  SESSION_REMOTE_FORM_DEFAULTS,
  FIELD_EVENT_TYPE_SELECT_OPTIONS,
} from '@/components/sessions/session-configs';
import { useCreateSession, useUpdateSession } from '@/hooks/useSessionsRemote';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  createSessionRemoteValidationSchema,
  editSessionRemoteValidationSchema,
} from '@/utils/validation-schemas-utils';
import {
  addOneHour,
  animatorRoleCodesForTab,
  appendUniqueById,
  buildCreateSessionDto,
  buildCreateValidationContext,
  buildEditValidationContext,
  buildUpdateDto,
  calculateEventEndDate,
  computeBudgetTotals,
  computeParticipantIds,
  emptyBudgetPost,
  getCreateEtpCount,
  getEditEtpCount,
  getMinTimeForDate,
  initializeFormState,
  isCoordinatorRole,
  isRecruiterRole,
  patchBudgetPost,
  removeById,
  syncIncludeMeSelected,
  todayDateString,
  toggleIncludeMeAnimatorChips,
} from '@/components/sessions/remote/session-form-utils';
import type { BudgetPost } from '@/types/inperson/session.types';
import type { ParticipantChip, SessionFormRecord } from '@/types/remote/session.types';
import type { SelectedUser } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Remote tab this form creates and edits. */
const SESSION_TAB = 'field-event' as const;

/** Props for {@link FieldEventForm} (`create` vs `edit` discriminated). */
export type FieldEventFormProps =
  | {
      mode: 'create';
      onClose: () => void;
      onSuccess: () => void;
    }
  | {
      mode: 'edit';
      session: SessionFormRecord;
      sessionId: string;
      onClose: () => void;
      onSuccess: () => void;
    };

/** Session form (field event). */
export const FieldEventForm: React.FC<FieldEventFormProps> = (props) => {
  if (props.mode === 'edit') {
    return <EditFieldEventForm {...props} />;
  }
  return <CreateFieldEventForm {...props} />;
};

/** One editable budget line on the field-event form. */
function BudgetPostRow({
  post,
  index,
  onPatch,
  onRemove,
}: Readonly<{
  post: BudgetPost;
  index: number;
  onPatch: (index: number, patch: Partial<BudgetPost>) => void;
  onRemove: (index: number) => void;
}>) {
  return (
    <div className="flex gap-2 items-end p-3 rounded-lg bg-gray-50">
      <div className="flex-1">
        <Select
          value={post.type}
          onChange={(type) => onPatch(index, { type })}
          options={BUDGET_POST_SELECT_OPTIONS}
        />
      </div>
      <div className="w-28">
        <Input
          type="number"
          value={post.amount}
          onChange={(e) => onPatch(index, { amount: Number.parseFloat(e.target.value) || 0 })}
          placeholder="€"
          min={0}
        />
      </div>
      <Button variant="danger" size="sm" onClick={() => onRemove(index)}>
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

/** Create form for a remote field event. */
function CreateFieldEventForm({
  onClose,
  onSuccess,
}: Readonly<Extract<FieldEventFormProps, { mode: 'create' }>>) {
  const createMutation = useCreateSession();
  const currentUser = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const isCoordinator = isCoordinatorRole(roles);
  const isRecruiter = isRecruiterRole(roles);
  const validation = useFormValidation(createSessionRemoteValidationSchema);
  const availableTypes = getSessionTypesForTab(SESSION_TAB);
  const defaultType = availableTypes[0];

  const [selectedType, setSelectedType] = React.useState(defaultType?.type || '');
  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [selectedAnimators, setSelectedAnimators] = React.useState<SelectedUser[]>([]);
  const [animatorAutocompleteValue, setAnimatorAutocompleteValue] = React.useState('');
  const [animatorResetKey, setAnimatorResetKey] = React.useState(0);
  const [includeMe, setIncludeMe] = React.useState(isRecruiter);
  const [eventDays, setEventDays] = React.useState(1);
  const [eventType, setEventType] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [budgetPosts, setBudgetPosts] = React.useState<BudgetPost[]>([]);
  const [etpUnitCost, setEtpUnitCost] = React.useState<number>(
    SESSION_REMOTE_FORM_DEFAULTS.etpUnitCost,
  );
  const [internalNotes, setInternalNotes] = React.useState('');

  const {
    options: animatorOptions,
    isLoading: animatorsLoading,
    onSearch: onAnimatorSearch,
  } = useAdminAutocomplete({ roleCodes: animatorRoleCodesForTab(SESSION_TAB) });
  const filteredAnimatorOptions = React.useMemo(
    () => animatorOptions.filter((o) => !selectedAnimators.some((a) => a.id === o.value)),
    [animatorOptions, selectedAnimators],
  );

  React.useEffect(() => {
    setSelectedAnimators((prev) => syncIncludeMeSelected(includeMe, currentUser, prev));
  }, [includeMe, currentUser]);

  const validationCtx = buildCreateValidationContext({
    selectedType,
    sessionDate,
    startTime,
    tutorId: '',
    studentId: '',
    managedById: null,
    recurrenceType: 'none',
    endTime,
    programId: '',
    eventType,
    sessionType: SESSION_TAB,
    isPrefilledFromMatching: false,
    animatorsCount: selectedAnimators.length,
    singleAnimatorId: '',
  });

  React.useEffect(() => {
    for (const field of Object.keys(createSessionRemoteValidationSchema)) {
      validation.validateField(
        field,
        validationCtx[field as keyof typeof validationCtx],
        validationCtx,
      );
    }
  }, [selectedType, sessionDate, startTime, endTime, eventType, selectedAnimators.length]);

  const todayStr = todayDateString();
  /** Changing start time fills end with start + 1h when end is empty. */
  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value) setEndTime(addOneHour(value));
  };

  /** Append a unique animator chip from the autocomplete value. */
  const handleAddAnimator = (value: string) => {
    if (!value || selectedAnimators.some((a) => a.id === value)) return;
    const option = animatorOptions.find((o) => o.value === value);
    if (option) {
      setSelectedAnimators((prev) => appendUniqueById(prev, { id: value, label: option.label }));
    }
    setAnimatorAutocompleteValue('');
    setAnimatorResetKey((k) => k + 1);
  };

  const etpCount = getCreateEtpCount(SESSION_TAB, '', selectedAnimators.length);
  const { budgetPostsTotal, etpTotal, budgetGrandTotal } = computeBudgetTotals(
    budgetPosts,
    etpCount,
    etpUnitCost,
  );

  /** Validate, then POST create or PATCH edit. */
  const handleSubmit = async () => {
    if (!validation.validateForm(validationCtx)) return;
    const mapping = availableTypes.find((t) => t.type === selectedType);
    if (!mapping) return;

    const dto = buildCreateSessionDto({
      mapping,
      sessionType: SESSION_TAB,
      sessionDate,
      startTime,
      endTime,
      recurrenceType: 'none',
      planDuringHolidays: false,
      tutorId: '',
      studentId: '',
      selectedTutorParticipants: [],
      selectedStudentParticipants: [],
      singleAnimatorId: '',
      selectedAnimators,
      managedById: null,
      maxParticipants: 0,
      internalNotes,
      eventType,
      location,
      budgetPosts,
      etpCount,
      etpUnitCost,
      budgetGrandTotal,
      programId: '',
    });

    try {
      await createMutation.mutateAsync(dto as never);
      toast.success('Séance créée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un événement terrain"
      subtitle="Renseignez les informations de l'événement terrain"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        {availableTypes.length > 1 && (
          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
              Configuration de la séance
            </h3>
            <Select
              label="Type de séance"
              value={selectedType}
              onChange={setSelectedType}
              options={availableTypes.map((t) => ({ value: t.type, label: t.label }))}
            />
          </div>
        )}

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-3-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-3-nuance-1)' }}>
            Informations générales
          </h3>
          <div>
            <span className="block text-sm font-medium mb-2">
              Animateur(s) <span className="text-red-500">*</span>
            </span>
            <Autocomplete
              key={animatorResetKey}
              placeholder="Rechercher un animateur..."
              options={filteredAnimatorOptions}
              value={animatorAutocompleteValue}
              onChange={handleAddAnimator}
              onSearch={onAnimatorSearch}
              isLoading={animatorsLoading}
              minSearchLength={2}
            />
            {selectedAnimators.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedAnimators.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                    style={{
                      background: 'color-mix(in srgb, var(--color-1-nuance-1) 12%, transparent)',
                      color: 'var(--color-1-nuance-1)',
                      border: '1px solid var(--color-1-nuance-3)',
                    }}
                  >
                    {a.label}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAnimators((prev) => removeById(prev, a.id));
                        if (a.id === currentUser?.id) setIncludeMe(false);
                      }}
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {(isCoordinator || isRecruiter) && (
              <Checkbox
                id="include-me"
                checked={includeMe}
                onCheckedChange={(checked) => setIncludeMe(Boolean(checked))}
                label="S'inclure en tant qu'animateur"
                className="mt-2"
              />
            )}
          </div>
        </div>

        <EventPeriodAndBudget
          sessionDate={sessionDate}
          setSessionDate={setSessionDate}
          eventDays={eventDays}
          setEventDays={setEventDays}
          calculatedEndDate={calculateEventEndDate(sessionDate, eventDays)}
          startTime={startTime}
          endTime={endTime}
          setEndTime={setEndTime}
          handleStartTimeChange={handleStartTimeChange}
          getMinTime={() => getMinTimeForDate(sessionDate, todayStr)}
          todayStr={todayStr}
          eventType={eventType}
          setEventType={setEventType}
          location={location}
          setLocation={setLocation}
          budgetPosts={budgetPosts}
          setBudgetPosts={setBudgetPosts}
          budgetPostsTotal={budgetPostsTotal}
          etpCount={etpCount}
          etpUnitCost={etpUnitCost}
          setEtpUnitCost={setEtpUnitCost}
          etpTotal={etpTotal}
          budgetGrandTotal={budgetGrandTotal}
          requireDate
        />

        <div
          className="space-y-2 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <Textarea
            label="Notes internes (optionnel)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
}

/** Edit form for an existing remote field event. */
function EditFieldEventForm({
  session,
  sessionId,
  onClose,
  onSuccess,
}: Readonly<Extract<FieldEventFormProps, { mode: 'edit' }>>) {
  const updateMutation = useUpdateSession();
  const currentUser = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const isCoordinator = isCoordinatorRole(roles);
  const isRecruiter = isRecruiterRole(roles);
  const validation = useFormValidation(editSessionRemoteValidationSchema);

  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [internalNotes, setInternalNotes] = React.useState('');
  const [includeMe, setIncludeMe] = React.useState(isRecruiter);
  const [selectedAnimators, setSelectedAnimators] = React.useState<ParticipantChip[]>([]);
  const [animatorResetKey, setAnimatorResetKey] = React.useState(0);
  const [eventType, setEventType] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [budgetPosts, setBudgetPosts] = React.useState<BudgetPost[]>([]);
  const [etpUnitCost, setEtpUnitCost] = React.useState<number>(
    SESSION_REMOTE_FORM_DEFAULTS.etpUnitCost,
  );

  const {
    options: animatorOptions,
    isLoading: isLoadingAnimators,
    onSearch: onSearchAnimators,
  } = useAdminAutocomplete({
    roleCodes: animatorRoleCodesForTab(SESSION_TAB),
  });

  React.useEffect(() => {
    initializeFormState(session, {
      setSessionDate,
      setStartTime,
      setEndTime,
      setRecurrenceType: () => undefined,
      setPlanDuringHolidays: () => undefined,
      setInternalNotes,
      setMaxParticipants: () => undefined,
      setSelectedTutor: () => undefined,
      setSelectedStudent: () => undefined,
      setSingleAnimatorId: () => undefined,
      setSingleAnimatorName: () => undefined,
      setSelectedAnimators,
      setSelectedTutorParticipants: () => undefined,
      setSelectedStudentParticipants: () => undefined,
      setManagedById: () => undefined,
      setProgramId: () => undefined,
      setEventType,
      setLocation,
      setBudgetPosts,
      setEtpUnitCost,
    });
    validation.validateInitialFormData({
      sessionDate: toLocalDateString(new Date(session.sessionDate)),
      startTime: session.startTime,
      managedById: null,
      programId: '',
      _isTutoring: false,
    });
  }, [session]);

  React.useEffect(() => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    validation.validateField('sessionDate', sessionDate, ctx);
    validation.validateField('startTime', startTime, ctx);
  }, [sessionDate, startTime]);

  /** Toggle the current user on or off the animator list. */
  const handleIncludeMeChange = React.useCallback(
    (checked: boolean) => {
      setIncludeMe(checked);
      setSelectedAnimators((prev) => toggleIncludeMeAnimatorChips(checked, currentUser, prev));
    },
    [currentUser],
  );

  const etpCount = getEditEtpCount(session.type, '', selectedAnimators.length);
  const { budgetPostsTotal, etpTotal, budgetGrandTotal } = computeBudgetTotals(
    budgetPosts,
    etpCount,
    etpUnitCost,
  );

  /** Validate, then POST create or PATCH edit. */
  const handleSubmit = async () => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    if (!validation.validateForm(ctx)) return;

    const { tutorIds, studentIds, animatorIds } = computeParticipantIds({
      session,
      isTutoring: false,
      selectedTutor: null,
      selectedStudent: null,
      selectedTutorParticipants: [],
      selectedStudentParticipants: [],
      selectedAnimators,
      singleAnimatorId: '',
    });

    const dto = buildUpdateDto({
      session,
      sessionDate,
      startTime,
      endTime,
      recurrenceType: session.recurrenceType || '',
      planDuringHolidays: false,
      internalNotes,
      maxParticipants: 0,
      tutorIds,
      studentIds,
      animatorIds,
      managedById: null,
      programId: '',
      eventType,
      location,
      budgetPosts,
      etpCount,
      etpUnitCost,
      budgetGrandTotal,
      updateFutureOccurrences: false,
    });

    try {
      await updateMutation.mutateAsync({ id: sessionId, dto });
      toast.success('Séance modifiée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'événement terrain"
      subtitle="Modifiez les informations de l'événement terrain"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker label="Date" value={sessionDate} onChange={setSessionDate} required />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              value={startTime}
              onChange={setStartTime}
              required
              step={1}
            />
            <TimePicker label="Heure de fin" value={endTime} onChange={setEndTime} step={1} />
          </div>
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Animateurs ({selectedAnimators.length})
          </h3>
          <Autocomplete
            key={animatorResetKey}
            placeholder="Ajouter un animateur..."
            options={animatorOptions.filter(
              (opt) => !selectedAnimators.some((a) => a.id === opt.value),
            )}
            value=""
            onChange={(val) => {
              const option = animatorOptions.find((o) => o.value === val);
              if (option && !selectedAnimators.some((a) => a.id === option.value)) {
                setSelectedAnimators((prev) => [
                  ...prev,
                  { id: option.value, name: option.label, role: 'recruiter' },
                ]);
                setAnimatorResetKey((k) => k + 1);
              }
            }}
            onSearch={onSearchAnimators}
            isLoading={isLoadingAnimators}
            minSearchLength={2}
          />
          {selectedAnimators.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedAnimators.map((animator) => (
                <div
                  key={animator.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-1-nuance-1)] text-white"
                >
                  <span className="text-sm">{animator.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAnimators((prev) => removeById(prev, animator.id));
                      if (animator.id === currentUser?.id) setIncludeMe(false);
                    }}
                    className="hover:bg-white/20 rounded p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(isCoordinator || isRecruiter) && (
            <Checkbox
              id="include-me-edit"
              checked={includeMe}
              onCheckedChange={(checked) => handleIncludeMeChange(Boolean(checked))}
              label="S'inclure en tant qu'animateur"
            />
          )}
        </div>

        <EventOrganisationAndBudget
          eventType={eventType}
          setEventType={setEventType}
          location={location}
          setLocation={setLocation}
          budgetPosts={budgetPosts}
          setBudgetPosts={setBudgetPosts}
          budgetPostsTotal={budgetPostsTotal}
          etpCount={etpCount}
          etpUnitCost={etpUnitCost}
          setEtpUnitCost={setEtpUnitCost}
          etpTotal={etpTotal}
          budgetGrandTotal={budgetGrandTotal}
          eventTypeRequired={false}
        />

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Notes internes
          </h3>
          <Textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
}

/** Dates, times, location, and budget posts shared by create and edit. */
function EventPeriodAndBudget({
  sessionDate,
  setSessionDate,
  eventDays,
  setEventDays,
  calculatedEndDate,
  startTime,
  endTime,
  setEndTime,
  handleStartTimeChange,
  getMinTime,
  todayStr,
  eventType,
  setEventType,
  location,
  setLocation,
  budgetPosts,
  setBudgetPosts,
  budgetPostsTotal,
  etpCount,
  etpUnitCost,
  setEtpUnitCost,
  etpTotal,
  budgetGrandTotal,
  requireDate,
}: Readonly<{
  sessionDate: string;
  setSessionDate: (v: string) => void;
  eventDays: number;
  setEventDays: (v: number) => void;
  calculatedEndDate: string;
  startTime: string;
  endTime: string;
  setEndTime: (v: string) => void;
  handleStartTimeChange: (v: string) => void;
  getMinTime: () => string | undefined;
  todayStr: string;
  eventType: string;
  setEventType: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  budgetPosts: BudgetPost[];
  setBudgetPosts: React.Dispatch<React.SetStateAction<BudgetPost[]>>;
  budgetPostsTotal: number;
  etpCount: number;
  etpUnitCost: number;
  setEtpUnitCost: (v: number) => void;
  etpTotal: number;
  budgetGrandTotal: number;
  requireDate: boolean;
}>) {
  return (
    <>
      <div
        className="space-y-4 p-4 rounded-lg border"
        style={{ borderColor: 'var(--color-2-nuance-3)' }}
      >
        <h3 className="font-semibold" style={{ color: 'var(--color-2-nuance-1)' }}>
          Période de l'événement
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            label="Date de début"
            required={requireDate}
            value={sessionDate}
            onChange={setSessionDate}
            minDate={todayStr}
          />
          <Input
            label="Nombre de jours"
            type="number"
            value={eventDays}
            onChange={(e) => setEventDays(Number.parseInt(e.target.value))}
            min={1}
            max={30}
          />
        </div>
        {sessionDate && eventDays > 0 && (
          <p className="text-sm text-gray-500">
            Date de fin calculée : <strong>{calculatedEndDate}</strong>
          </p>
        )}
        <div className="grid grid-cols-2 gap-4">
          <TimePicker
            label="Heure de début"
            value={startTime}
            onChange={handleStartTimeChange}
            minTime={getMinTime()}
            step={1}
          />
          <TimePicker
            label="Heure de fin"
            value={endTime}
            onChange={setEndTime}
            minTime={startTime || undefined}
            step={1}
          />
        </div>
      </div>
      <EventOrganisationAndBudget
        eventType={eventType}
        setEventType={setEventType}
        location={location}
        setLocation={setLocation}
        budgetPosts={budgetPosts}
        setBudgetPosts={setBudgetPosts}
        budgetPostsTotal={budgetPostsTotal}
        etpCount={etpCount}
        etpUnitCost={etpUnitCost}
        setEtpUnitCost={setEtpUnitCost}
        etpTotal={etpTotal}
        budgetGrandTotal={budgetGrandTotal}
      />
    </>
  );
}

/** Event type, location, and budget posts shared by create and edit. */
function EventOrganisationAndBudget({
  eventType,
  setEventType,
  location,
  setLocation,
  budgetPosts,
  setBudgetPosts,
  budgetPostsTotal,
  etpCount,
  etpUnitCost,
  setEtpUnitCost,
  etpTotal,
  budgetGrandTotal,
  eventTypeRequired = true,
}: Readonly<{
  eventType: string;
  setEventType: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  budgetPosts: BudgetPost[];
  setBudgetPosts: React.Dispatch<React.SetStateAction<BudgetPost[]>>;
  budgetPostsTotal: number;
  etpCount: number;
  etpUnitCost: number;
  setEtpUnitCost: (v: number) => void;
  etpTotal: number;
  budgetGrandTotal: number;
  eventTypeRequired?: boolean;
}>) {
  return (
    <>
      <div
        className="space-y-4 p-4 rounded-lg border"
        style={{ borderColor: 'var(--color-3-nuance-3)' }}
      >
        <h3 className="font-semibold text-lg" style={{ color: 'var(--color-3-nuance-1)' }}>
          Organisation
        </h3>
        <Select
          label="Type d'événement"
          value={eventType}
          onChange={setEventType}
          options={FIELD_EVENT_TYPE_SELECT_OPTIONS}
          required={eventTypeRequired}
        />
        <Input
          label="Lieu"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Nom de l'établissement ou du lieu"
        />
      </div>

      <div
        className="space-y-4 p-4 rounded-lg border"
        style={{ borderColor: 'color-mix(in srgb, var(--color-1-nuance-1) 30%, transparent)' }}
      >
        <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
          Budget prévu
        </h3>
        <div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium">Postes budgétaires</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setBudgetPosts((p) => [...p, emptyBudgetPost()])}
            >
              + Ajouter un poste
            </Button>
          </div>
          {budgetPosts.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun poste ajouté</p>
          ) : (
            <div className="space-y-2">
              {budgetPosts.map((post, i) => (
                <BudgetPostRow
                  key={`${post.type}-${i}`}
                  post={post}
                  index={i}
                  onPatch={(index, patch) =>
                    setBudgetPosts((prev) => patchBudgetPost(prev, index, patch))
                  }
                  onRemove={(index) =>
                    setBudgetPosts((prev) => prev.filter((_, idx) => idx !== index))
                  }
                />
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-between items-center p-3 rounded-lg bg-gray-100 text-sm">
            <span className="font-medium">Total postes</span>
            <span className="font-bold">{budgetPostsTotal.toFixed(2)} €</span>
          </div>
        </div>
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-3">Coût animateurs (ETP)</h4>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <span className="block text-xs text-gray-500 mb-1">Nombre ETP (auto)</span>
              <div className="px-3 py-2 border rounded-lg bg-gray-50 text-sm font-medium">
                {etpCount}
              </div>
            </div>
            <Input
              label="Coût unitaire €"
              type="number"
              value={etpUnitCost}
              onChange={(e) => setEtpUnitCost(Number.parseFloat(e.target.value) || 0)}
              min={0}
            />
          </div>
          <div className="flex justify-between items-center p-3 rounded-lg bg-gray-100 text-sm">
            <span className="font-medium">Total ETP</span>
            <span className="font-bold">{etpTotal.toFixed(2)} €</span>
          </div>
        </div>
        <div
          className="flex justify-between items-center p-4 rounded-lg"
          style={{ background: 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)' }}
        >
          <span className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
            Budget total
          </span>
          <span className="text-2xl font-bold" style={{ color: 'var(--color-1-nuance-1)' }}>
            {budgetGrandTotal.toFixed(2)} €
          </span>
        </div>
      </div>
    </>
  );
}
